/**
 * The three states of a read — spec v6b §2, `docs/adr/9013`.
 *
 * | state | when | the screen |
 * |---|---|---|
 * | `awaiting`    | 200 with nothing in it                  | the silhouette + ONE neutral strip |
 * | `populated`   | 200 with rows                           | the final form |
 * | `unavailable` | a refusal or failure (404, 500, 401…)   | the silhouette + a strip that says so, the code in "Details" |
 *
 * plus `loading`, which is not a finding at all.
 *
 * Every screen was going to derive this from a query result, and every screen would have
 * derived it slightly differently — which is how `AsyncRegion` came to be promoted out of five
 * chapters (ADR 0015). So it is derived once, here.
 *
 * **Ghost is not zero** (leader ruling 5). `awaiting` carries the (empty) data, so a screen
 * can still read the run it asked about, but nothing downstream should compute a figure from
 * it: a count over an empty list is `0`, and `0 transfers` states a finding nobody has made.
 *
 * **Unavailable has three reasons, and they are not one.** A 401/403 is the no-permission
 * surface (G2 §2.6). A 404 the platform answers ON PURPOSE for a read it has not built
 * (`NOT_BUILT_PROBLEM_CODES`) is ADR 0015's fifth state — "not published yet", not a fault to
 * report. Everything else is a failure and earns the critical strip. The designer's table
 * says "404 / 500 → critical"; the distinction is kept because audit v4 G-04 found exactly
 * that deliberate 404 rendered as a defect, and telling a planner to report a roadmap item is
 * the regression ADR 0015's amendment closed.
 */

import { ApiMalformedProblemError, ApiProblemError, isNotBuiltProblem, isPermissionProblem } from '../api';

export type UnavailableReason = 'failed' | 'not-built' | 'no-permission';

export type ReadState<T> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'awaiting'; readonly data: T }
  | { readonly kind: 'populated'; readonly data: T }
  | {
    readonly kind: 'unavailable';
    readonly reason: UnavailableReason;
    /** The HTTP status, where the service answered at all. */
    readonly status: number | null;
    /** The RFC 9457 problem code, where the answer carried one. */
    readonly code: string | null;
    readonly error: unknown;
  };

/**
 * The query, typed STRUCTURALLY — the same shape `AsyncRegion` takes — so a test can hand it a
 * plain object rather than mounting a `QueryClientProvider`.
 */
export interface QueryLike<T> {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
}

/**
 * Empty, for the two shapes the pinned contract answers with: a bare array, or an envelope
 * with `items`. Anything else has no default — a verdict object is not "empty" by any rule
 * this module could guess — and the caller must pass `isEmpty`.
 */
export function isEmptyRead(data: unknown): boolean {
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === 'object' && data !== null && 'items' in data) {
    const items = (data as { readonly items: unknown }).items;
    if (Array.isArray(items)) return items.length === 0;
  }
  throw new Error(
    'readState: this response is neither an array nor an `items` envelope, so what "empty" means '
    + 'for it is the caller\'s to say — pass `isEmpty`.',
  );
}

/**
 * One query's state. `isEmpty` defaults to `isEmptyRead`.
 *
 * A DISABLED query — a screen with no `?runId=` yet — is `isPending` forever in TanStack
 * Query v5 and so reads as `loading` here. That is deliberate: "no run selected" is not a
 * state of the read, it is a state of the screen's scope, and `ScopeRequired` renders it
 * before a read is ever asked for (CLAUDE.md §3.4).
 */
export function readState<T>(query: QueryLike<T>, isEmpty: (data: T) => boolean = isEmptyRead): ReadState<T> {
  if (query.isError) return unavailable(query.error);
  if (query.isPending || query.data === undefined) return { kind: 'loading' };
  return isEmpty(query.data) ? { kind: 'awaiting', data: query.data } : { kind: 'populated', data: query.data };
}

function unavailable<T>(error: unknown): ReadState<T> {
  const status = error instanceof ApiProblemError || error instanceof ApiMalformedProblemError ? error.status : null;
  const code = error instanceof ApiProblemError ? error.code : null;
  return { kind: 'unavailable', reason: reasonOf(error), status, code, error };
}

function reasonOf(error: unknown): UnavailableReason {
  if (isPermissionProblem(error)) return 'no-permission';
  if (isNotBuiltProblem(error)) return 'not-built';
  return 'failed';
}

/** The order a screen's ONE strip reports in: the worst state its reads are in. */
const SEVERITY: Readonly<Record<ReadState<unknown>['kind'], number>> = {
  populated: 0,
  awaiting: 1,
  loading: 2,
  unavailable: 3,
};

const REASON_SEVERITY: Readonly<Record<UnavailableReason, number>> = {
  'not-built': 0,
  'no-permission': 1,
  failed: 2,
};

/**
 * The state a screen with several reads reports — ONE strip per screen, not one per panel
 * (spec v6b §2). The most severe wins: a failure over a refusal over a silence over data.
 * Each block still ghosts on its OWN read; this only picks what the strip says.
 */
export function combineReadStates(...states: readonly ReadState<unknown>[]): ReadState<unknown> {
  if (states.length === 0) throw new Error('combineReadStates: no state to combine.');
  return states.reduce((worst, next) => {
    const a = SEVERITY[worst.kind];
    const b = SEVERITY[next.kind];
    if (b !== a) return b > a ? next : worst;
    if (worst.kind === 'unavailable' && next.kind === 'unavailable') {
      return REASON_SEVERITY[next.reason] > REASON_SEVERITY[worst.reason] ? next : worst;
    }
    return worst;
  });
}

/** Whether a block draws its silhouette: every state but `populated`. */
export function isGhost(state: ReadState<unknown>): boolean {
  return state.kind !== 'populated';
}
